import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import ThemeToggle from '../components/ThemeToggle';

export default function LoginPage() {
  const { t } = useLang();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      setError(t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background" data-testid="login-page">
      {/* ============ BRAND PANEL (always dark, intentional) ============ */}
      <div
        className="relative hidden lg:flex lg:w-1/2 flex-col justify-between overflow-hidden p-12"
        style={{
          background: 'radial-gradient(circle at 30% 20%, hsl(38 60% 14%) 0%, hsl(240 22% 5%) 55%, hsl(240 24% 3%) 100%)',
          color: '#F0F0F8',
        }}
      >
        <div aria-hidden className="absolute inset-0 opacity-40" style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }} />

        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full opacity-50 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(212, 168, 67, 0.30), transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full opacity-40 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.20), transparent 70%)' }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/95 shadow-lg overflow-hidden ring-1 ring-white/15">
            <img src="/lovi-logo.png" alt="Lovi Creation" className="h-11 w-11 object-contain" data-testid="login-brand-logo" />
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-semibold tracking-tight">Lovi Creation</div>
            <div className="text-[11px] font-medium text-white/45">CPN Trade Inc</div>
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-start">
          <div
            className="mb-9 flex h-44 w-44 items-center justify-center rounded-3xl bg-white/[0.97] shadow-[0_30px_80px_-10px_rgba(212,168,67,0.45)] ring-1 ring-white/20 glow-gold"
            data-testid="login-hero-logo"
          >
            <img src="/lovi-logo-full.png" alt="Lovi Creation — CPN Trade Inc" className="h-40 w-40 object-contain" />
          </div>
          <h1 className="text-[40px] font-extrabold tracking-tight text-white leading-[1.05]">
            {t('app_title')}
          </h1>
          <p className="mt-3 max-w-md text-[15px] text-white/55 leading-relaxed">
            <span className="text-gold-gradient font-semibold">TikTok Shop</span>
            <span className="mx-2 text-white/30">→</span>
            <span className="text-gold-gradient font-semibold">QuickBooks Online</span>
          </p>
          <div className="mt-9 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-medium text-emerald-400">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            POC verified — 44/44 tests passing
          </div>
        </div>

        <div className="relative z-10 text-[11px] text-white/35">
          © Olimpo Web Design LLC · Lovi Creation
        </div>
      </div>

      {/* ============ FORM PANEL (adaptive) ============ */}
      <div className="flex flex-1 flex-col bg-background relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 h-[300px] w-[300px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(212,168,67,0.30), transparent 70%)' }}
        />
        <div className="relative flex items-center justify-end gap-2 px-6 py-4">
          <LanguageToggle />
          <ThemeToggle />
        </div>
        <div className="relative flex flex-1 items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            <div className="flex flex-col items-center mb-8 lg:hidden">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white shadow-lg" style={{ border: '1px solid var(--border-default)' }}>
                <img src="/lovi-logo-full.png" alt="Lovi" className="h-16 w-16 object-contain" />
              </div>
            </div>

            <div
              className="rounded-[20px] p-10"
              style={{
                background: 'hsl(var(--surface))',
                border: '1px solid var(--border-default)',
                boxShadow: 'var(--shadow-lg)',
              }}
            >
              <div className="mb-7">
                <div
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.14em] mb-3"
                  style={{
                    border: '1px solid rgba(212,168,67,0.30)',
                    background: 'rgba(212,168,67,0.06)',
                    color: 'hsl(var(--primary))',
                  }}
                >
                  <Sparkles className="h-3 w-3" strokeWidth={2.2} />
                  Acceso seguro
                </div>
                <h2 className="text-2xl font-extrabold text-foreground tracking-tight" style={{ letterSpacing: '-0.02em' }}>
                  {t('login.title')}
                </h2>
                <p className="mt-1.5 text-sm text-muted-foreground">{t('app_subtitle')}</p>
              </div>

              <form className="space-y-5" onSubmit={submit}>
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                  >
                    {t('login.email')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                  >
                    {t('login.password')}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                {error && (
                  <div
                    className="rounded-lg px-3.5 py-2.5 text-sm"
                    style={{
                      border: '1px solid var(--danger-border)',
                      background: 'var(--danger-bg)',
                      color: 'var(--danger-fg)',
                    }}
                    data-testid="login-error"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  size="lg"
                  className="w-full"
                  disabled={loading}
                  data-testid="login-submit-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('login.submit')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
